<template>
  <div
    id="document-header"
    class="document-header d-flex w-100 justify-content-between align-items-center"
    data-testid="document-header"
  >
    <div class="d-flex flex-row">
      <i-icon-button
        class="back-button"
        data-testid="results-panel-back-button"
        symbol="medium-arrow-left"
        type="transparent"
        tooltip-container="document-header"
        @button-click="$emit('back')"
      ></i-icon-button>
      <div
        class="document-icon i-mr-6 d-flex align-items-center justify-content-center"
      >
        <i-icon
          class="i-text-primary-110"
          symbol="small-file"
          :size="16"
        ></i-icon>
      </div>
      <div class="d-flex flex-column">
        <span
          class="customer-name fs-exclude i-text-subheading-3"
          @click="$emit('back', true)"
        >
          {{ $store.state.customers.selectedCustomer.name }}
        </span>
        <div class="customer-second-line d-flex flex-row">
          <div
            class="document-switcher-trigger d-flex flex-row"
            @click="switcherOpen = true"
            @mouseenter="hoveringTitle = true"
            @mouseleave="hoveringTitle = false"
          >
            <span
              class="document-name fs-exclude i-text-button-2 i-text-neutral-120"
              data-testid="document-switcher-toggle"
            >
              {{ document.name }}
            </span>
            <div class="chevron">
              <i-icon
                symbol="small-chevron-down"
                :class="{
                  'i-text-neutral-100': hoveringTitle,
                  'i-text-neutral-80': !hoveringTitle,
                }"
                :size="20"
              ></i-icon>
            </div>
          </div>
          <document-switcher
            class="document-switcher"
            :document="document"
            :show="switcherOpen"
            @hide-panel="switcherOpen = false"
          />
        </div>
      </div>
    </div>

    <div class="d-flex align-items-center">
      <i-icon-button
        id="header-button-api"
        data-testid="view-document-api"
        symbol="medium-file-code"
        type="transparent"
        size="md"
        @button-click="openAPIResponse"
      ></i-icon-button>
      <i-tooltip
        target="#header-button-api"
        title="View API Response"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <i-icon-button
        v-if="documentStatusEnabled"
        id="header-button-history"
        data-testid="document-history"
        symbol="medium-clock-rewind"
        type="transparent"
        size="md"
        @button-click="toggleHistory"
      ></i-icon-button>
      <i-tooltip
        target="#header-button-history"
        title="Status History"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <i-icon-button
        id="header-button-download"
        data-testid="download-document"
        class="download-document-button"
        symbol="medium-download"
        type="transparent"
        size="md"
        @button-click="downloadDocument"
      ></i-icon-button>
      <i-tooltip
        target="#header-button-download"
        title="Download"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <i-icon-button
        id="header-button-delete"
        data-testid="delete-document"
        class="delete-document-button"
        symbol="medium-delete-bin-1"
        type="transparent"
        size="md"
        @button-click="tryDeleteDocument"
      ></i-icon-button>
      <i-tooltip
        target="#header-button-delete"
        title="Delete"
        :alignment="iTooltipAlignmentMode.BOTTOM_MIDDLE"
      />

      <document-status-selector
        v-if="documentStatusEnabled"
        :document="document"
        class="i-ml-8"
      ></document-status-selector>
      <customer-approval-buttons
        v-else
        class="customer-approval-buttons"
        :customer="$store.state.customers.selectedCustomer"
      />
    </div>
  </div>
</template>
<script>
import { downloadDocument } from '@/utils/download';
import CustomerApprovalButtons from '@/V2/customers/CustomerApprovalButtons';
import DocumentStatusSelector from '@/V2/documents/document-status/DocumentStatusSelector.vue';
import DocumentSwitcher from '@/V2/documents/DocumentSwitcher';
import {
  iIcon,
  iIconButton,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    CustomerApprovalButtons,
    DocumentStatusSelector,
    DocumentSwitcher,
    iIcon,
    iIconButton,
    iTooltip,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDownloading: false,
      isDeleting: false,
      switcherOpen: false,
      hoveringTitle: false,
      iTooltipAlignmentMode,
    };
  },
  computed: {
    ...mapGetters({
      documentStatusEnabled: 'documentStatusEnabled',
    }),
  },
  methods: {
    ...mapActions({
      deleteDocument: 'deleteDocument',
    }),
    openAPIResponse() {
      this.$router
        .push({
          name: 'document-api',
          params: {
            customer_id: this.$route.params.customer_id,
            document_id: this.$route.params.document_id,
          },
        })
        .catch(() => {});
    },
    downloadDocument() {
      if (!this.isDownloading) {
        this.isDownloading = true;
        downloadDocument(
          this.document.urls.original_file,
          (this.$store.state.currentUser.is_superuser ? 'INSCRIBE_DOC-' : '') +
            this.document.name,
        ).then(() => {
          this.isDownloading = false;
        });
      }
    },
    async tryDeleteDocument() {
      this.$emit('document-deleted');
      const customerId = this.$route.params.customer_id;
      await this.deleteDocument({
        customerId,
        document: this.document,
      });
      this.$router.push({
        name: 'customer-overview',
        params: {
          customer_id: customerId,
        },
      });
    },
    toggleHistory() {
      this.$emit('toggle-history');
    },
  },
};
</script>

<style lang="scss" scoped>
.document-header {
  height: 64px;
  z-index: 51;
  background: $color-white;
  border-bottom: 1px solid $color-almost-black-60;
  padding: 12px 24px 12px 12px;

  .back-button {
    padding-right: 12px;
  }

  .document-icon {
    width: 40px;
    height: 40px;
    background: $light-primary-30;
    border-radius: 100%;
  }

  .customer-name {
    color: $light-neutral-110;
    padding-bottom: 2px;
    cursor: pointer;

    &:hover {
      color: $light-neutral-120;
    }
  }

  .document-switcher-trigger {
    align-items: center;
    cursor: pointer;

    .chevron {
      padding-left: 4px;
    }
  }

  .customer-approval-buttons {
    margin-left: 12px;
  }
}
</style>
